import { useState } from "react";
import { SpaceBetween, Button, Box } from "@amzn/awsui-components-react";
import { Checkbox } from "@amzn/uxm-aws-ui-components-react/lib/src/polaris/";
import { CheckboxNode } from "../CheckboxFilter/CheckboxFilter";

/**
 * Custom hook for managing checkbox functionality
 *
 * @param {CheckboxNode} checkBox - The checkbox node
 * @param {React.Dispatch<React.SetStateAction<CheckboxNode[]>>} setCheckBoxStructure - Function to update checkbox structure
 * @returns Object containing checkbox handlers and state
 */
export function useCustomCheckbox(
    checkBox: CheckboxNode,
    setCheckBoxStructure: React.Dispatch<React.SetStateAction<CheckboxNode[]>>,
) {
    const [isExpanded, setIsExpanded] = useState<boolean>(false);

    const updateNodeAndChildren = (node: CheckboxNode, selected: boolean): CheckboxNode => {
        const updatedNode = { ...node, selected };
        if (node.children) {
            updatedNode.children = node.children.map((child) => updateNodeAndChildren(child, selected));
        }
        return updatedNode;
    };

    const updateParentSelection = (node: CheckboxNode): CheckboxNode => {
        if (!node.children || node.children.length === 0) {
            return node;
        }

        const updatedChildren = node.children.map(updateParentSelection);
        const allChildrenSelected = updatedChildren.every((child) => child.selected);
        const anyChildSelected = updatedChildren.some(
            (child) => child.selected || (child.children && child.children.some((grandchild) => grandchild.selected)),
        );

        return {
            ...node,
            children: updatedChildren,
            selected: allChildrenSelected && anyChildSelected,
        };
    };

    const updateNodeSelection = (nodes: CheckboxNode[], targetId: string, selected: boolean): CheckboxNode[] => {
        return nodes
            .map((node) => {
                if (node.id === targetId) {
                    return updateNodeAndChildren(node, selected);
                }
                if (node.children) {
                    return { ...node, children: updateNodeSelection(node.children, targetId, selected) };
                }
                return node;
            })
            .map(updateParentSelection);
    };

    const handleCheckboxChange = (value: boolean) => {
        setCheckBoxStructure((prevStructure) => updateNodeSelection(prevStructure, checkBox.id, value));
    };

    const indeterminateValid = (): boolean => {
        if (!checkBox.children || checkBox.children.length === 0) {
            return false;
        }

        const hasSelectedChildren = (children: CheckboxNode[]): boolean => {
            return children.some((child) => child.selected || (child.children && hasSelectedChildren(child.children)));
        };

        const allSelected = checkBox.children.every((child) => child.selected);
        const someSelected = hasSelectedChildren(checkBox.children);

        return someSelected && !allSelected;
    };

    const toggleExpansion = () => {
        setIsExpanded(!isExpanded);
    };

    return {
        isExpanded,
        handleCheckboxChange,
        indeterminateValid,
        toggleExpansion,
    };
}

/**
 * Icon types for the custom checkbox expand/collapse button
 *
 * @enum
 * @property {string} OPEN - Caret up icon for expanded state
 * @property {string} CLOSE - Caret down icon for collapsed state
 */
export enum CustomCheckboxIcon {
    OPEN = "caret-up",
    CLOSE = "caret-down",
}

/**
 * Props for the CustomCheckbox component
 *
 * @interface
 * @property {CheckboxNode} checkBox - The checkbox node to render
 * @property {CheckboxNode[]} checkBoxStructure - The complete checkbox structure array
 * @property {React.Dispatch<React.SetStateAction<CheckboxNode[]>>} setCheckBoxStructure - Function to update the checkbox structure
 */
export interface CustomCheckboxProps {
    checkBox: CheckboxNode;
    checkBoxStructure: CheckboxNode[];
    setCheckBoxStructure: React.Dispatch<React.SetStateAction<CheckboxNode[]>>;
    touchpoint: string;
}

/**
 * Props for the CustomCheckboxChildrenWrapper component
 *
 * @interface
 * @property {CheckboxNode[]} [children] - Array of child checkbox nodes to render
 * @property {CheckboxNode[]} checkBoxStructure - The complete checkbox structure array
 * @property {React.Dispatch<React.SetStateAction<CheckboxNode[]>>} setCheckBoxStructure - Function to update the checkbox structure
 */
export interface CustomCheckboxChildrenWrapperProps {
    children?: CheckboxNode[];
    checkBoxStructure: CheckboxNode[];
    setCheckBoxStructure: React.Dispatch<React.SetStateAction<CheckboxNode[]>>;
    touchpoint: string;
}

/**
 * Props for the CustomCheckboxChildrenExpandButton component
 *
 * @interface
 * @property {CheckboxNode[]} [children] - Array of child nodes to determine if button should be shown
 * @property {boolean} isExpanded - Current expansion state
 * @property {() => void} toggleExpansion - Function to toggle the expansion state
 * @property {string} testId - The text id for the button
 */
export interface CustomCheckboxChildrenExpandButtonProps {
    children?: CheckboxNode[];
    isExpanded: boolean;
    toggleExpansion: () => void;
    testId?: string;
}

/**
 * Renders the expand/collapse button for checkbox with children
 *
 * @param {CustomCheckboxChildrenExpandButtonProps} props - The props for the expand button component
 * @return {JSX.Element} - The rendered expand button or empty fragment if no children
 */
export function CustomCheckboxChildrenExpandButton({
    children,
    isExpanded,
    toggleExpansion,
    testId,
}: CustomCheckboxChildrenExpandButtonProps) {
    if (!children || children.length === 0) {
        return <></>;
    }

    return (
        <Button
            data-testid={`expand-button-${testId}`}
            iconName={isExpanded ? CustomCheckboxIcon.OPEN : CustomCheckboxIcon.CLOSE}
            variant="icon"
            onClick={toggleExpansion}
        />
    );
}

/**
 * Renders the children checkboxes in an indented wrapper
 *
 * @param {CustomCheckboxChildrenWrapperProps} props - The props for the children wrapper component
 * @return {JSX.Element} - The rendered children wrapper or empty fragment if no children
 */
export function CustomCheckboxChildrenWrapper({
    children,
    checkBoxStructure,
    setCheckBoxStructure,
    touchpoint,
}: CustomCheckboxChildrenWrapperProps) {
    if (!children || children.length === 0) {
        return <></>;
    }

    return (
        <Box margin={{ left: "xxl", top: "xxs" }}>
            <SpaceBetween size="xxs">
                {children.map((section) => (
                    <CustomCheckbox
                        key={section.id}
                        checkBox={section}
                        checkBoxStructure={checkBoxStructure}
                        setCheckBoxStructure={setCheckBoxStructure}
                        touchpoint={touchpoint}
                    />
                ))}
            </SpaceBetween>
        </Box>
    );
}

/**
 * Renders a hierarchical checkbox with expand/collapse functionality for children
 *
 * @param {CustomCheckboxProps} props - The props for the CustomCheckbox component
 * @return - The rendered custom checkbox component
 */
export default function CustomCheckbox(props: CustomCheckboxProps) {
    const { isExpanded, handleCheckboxChange, indeterminateValid, toggleExpansion } = useCustomCheckbox(
        props.checkBox,
        props.setCheckBoxStructure,
    );

    return (
        <>
            <SpaceBetween alignItems="center" size="xxxs" direction="horizontal">
                <Checkbox
                    data-testid={props.checkBox.id}
                    onChange={({ detail }) => handleCheckboxChange(detail.checked)}
                    checked={props.checkBox.selected ?? false}
                    indeterminate={indeterminateValid()}
                    touchpoint={`${props.touchpoint}-${props.checkBox.name}`}
                >
                    {props.checkBox.name}
                </Checkbox>
                <CustomCheckboxChildrenExpandButton
                    testId={props.checkBox.id}
                    children={props.checkBox.children}
                    isExpanded={isExpanded}
                    toggleExpansion={toggleExpansion}
                />
            </SpaceBetween>
            {isExpanded && (
                <CustomCheckboxChildrenWrapper
                    children={props.checkBox.children}
                    checkBoxStructure={props.checkBoxStructure}
                    setCheckBoxStructure={props.setCheckBoxStructure}
                    touchpoint={props.touchpoint}
                />
            )}
        </>
    );
}
